import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { savedItemsService } from '../services/api'
import { useTheme } from '../contexts/ThemeContext'
import { useNotifications } from '../contexts/NotificationContext'
import { useAuth } from '../contexts/AuthContext'
import { useCartsQuery } from '../hooks/useCartsQuery'
import { useSavedItemsQuery } from '../hooks/useSavedItemsQuery'
import { describeLibraryListState, savedItemsQueryKey } from '../utils/libraryIsolation'
import { getUserId } from '../utils/sessionUser'
import ProductImage from '../components/ProductImage'
import ConfirmModal from '../components/ConfirmModal'
import AppDock from '../components/AppDock'
import '../pages/LibraryPage.css'

function LibraryPage() {
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { token, user } = useAuth()
  const queryClient = useQueryClient()
  const { addNotification } = useNotifications()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
  const userId = getUserId(user)
  const savedKey = savedItemsQueryKey(userId)

  const { data: savedItemsData, isLoading, error } = useSavedItemsQuery({
    enabled: !!token,
  })
  const savedItems = Array.isArray(savedItemsData) ? savedItemsData : []
  const libraryState = describeLibraryListState({
    isLoading,
    data: savedItemsData,
    error,
    query: searchTerm,
  })

  const { data: cartsData } = useCartsQuery()
  const carts = cartsData ?? []

  const deleteMutation = useMutation({
    mutationFn: (savedId) => savedItemsService.delete(savedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedKey })
      setSelectedItems(new Set())
    },
  })

  const moveToCartMutation = useMutation({
    mutationFn: ({ savedId, cartId }) => savedItemsService.moveToCart(savedId, cartId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: savedKey })
      queryClient.invalidateQueries({ queryKey: ['carts'] })
      setSelectedItems(new Set())
    },
  })

  const filteredItems = savedItems.filter(item =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.certainty_label?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleDeleteSelected = async () => {
    if (selectedItems.size === 0) return
    const confirmed = await new Promise((resolve) => {
      setConfirmModal({
        isOpen: true,
        title: 'Confirmation',
        message: `Supprimer ${selectedItems.size} élément(s) de la bibliothèque ?`,
        onConfirm: () => {
          resolve(true)
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
        },
        onCancel: () => {
          resolve(false)
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
        }
      })
    })
    if (confirmed) {
      selectedItems.forEach(id => deleteMutation.mutate(id))
    }
  }

  const handleMoveToCart = (savedId) => {
    if (carts.length === 0) {
      addNotification({
        type: 'warning',
        title: 'Aucun panier',
        message: 'Aucun panier disponible. Créez d\'abord un panier.',
        showToast: true
      })
      return
    }
    const cartId = window.prompt(
      'ID du panier de destination:\n' + carts.map(c => `- ${c.name} (${c.id})`).join('\n')
    )
    if (cartId) {
      moveToCartMutation.mutate({ savedId, cartId })
    }
  }

  const toggleSelect = (id) => {
    const newSet = new Set(selectedItems)
    if (newSet.has(id)) {
      newSet.delete(id)
    } else {
      newSet.add(id)
    }
    setSelectedItems(newSet)
  }

  return (
    <div className="library-page">
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => {
          if (confirmModal.onCancel) confirmModal.onCancel()
          setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
        }}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
        title={confirmModal.title}
        message={confirmModal.message}
      />

      <header className="header">
        <button className="btn-secondary" onClick={() => navigate('/carts', { state: { activeApp: 'carts' } })}>
          ← Retour
        </button>
        <h1>📚 Bibliothèque — mis de côté</h1>
        <div className="header-actions">
          <input
            type="text"
            placeholder="Rechercher…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {selectedItems.size > 0 && (
            <button className="btn-danger" onClick={handleDeleteSelected}>
              Supprimer ({selectedItems.size})
            </button>
          )}
        </div>
      </header>

      <div className="library-container">
        {libraryState === 'loading' ? (
          <div className="loading">Chargement...</div>
        ) : libraryState === 'error' || libraryState === 'forbidden' ? (
          <div className="empty-state">
            <div className="empty-icon">⚠️</div>
            <h3>Bibliothèque indisponible</h3>
            <p>{error?.response?.data?.detail || error?.message || 'Impossible de charger la bibliothèque.'}</p>
          </div>
        ) : libraryState === 'empty' ? (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h3>Aucun élément sauvegardé</h3>
            <p>Les produits que vous mettez de côté apparaîtront ici</p>
          </div>
        ) : libraryState === 'search-empty' ? (
          <div className="empty-state">
            <div className="empty-icon">🔎</div>
            <h3>Aucun résultat</h3>
            <p>Aucun élément ne correspond à cette recherche</p>
          </div>
        ) : (
          <div className="saved-items-list">
            {filteredItems.map(item => (
              <div
                key={item.id}
                className={`saved-item-card ${selectedItems.has(item.id) ? 'selected' : ''}`}
                onClick={() => toggleSelect(item.id)}
              >
                <div className="item-image">
                  <ProductImage item={item} alt={item.name} />
                </div>
                <div className="item-info">
                  <h3>{item.name}</h3>
                  <p className="item-meta">
                    € {parseFloat(item.price || 0).toFixed(2)} × {item.quantity || 1}
                  </p>
                </div>
                <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn-primary"
                    onClick={() => handleMoveToCart(item.id)}
                    title="Ajouter au panier"
                  >
                    ➕
                  </button>
                  <button
                    className="btn-danger"
                    onClick={() => {
                      new Promise((resolve) => {
                        setConfirmModal({
                          isOpen: true,
                          title: 'Confirmation',
                          message: 'Supprimer de la bibliothèque ?',
                          onConfirm: () => {
                            resolve(true)
                            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
                          },
                          onCancel: () => {
                            resolve(false)
                            setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null, onCancel: null })
                          }
                        })
                      }).then((confirmed) => {
                        if (confirmed) {
                          deleteMutation.mutate(item.id)
                        }
                      })
                    }}
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dock de navigation */}
      <AppDock 
        onAppSelect={(appId) => {
          if (appId === 'house') {
            navigate('/carts', { state: { activeApp: 'house' } })
          } else if (appId === 'carts') {
            navigate('/carts', { state: { activeApp: 'carts' } })
          } else if (appId === 'analytics') {
            navigate('/carts', { state: { activeApp: 'analytics' } })
          } else if (appId === 'social') {
            navigate('/carts', { state: { activeApp: 'social' } })
          } else if (appId === 'settings') {
            navigate('/carts', { state: { activeApp: 'settings' } })
          }
        }} 
        selectedApp={null}
      />
    </div>
  )
}

export default LibraryPage














